import { IconDownload } from '@iwoca/icons';
import {
  GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse,
  GetApiIntroducerPortalRepaymentSimulatorResponse,
} from '@iwoca/lapi-client/introducer_portal';
import { Button } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { format } from 'date-fns';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { CSVLink } from 'react-csv';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

type TProps = {
  schedule:
    | GetApiIntroducerPortalRepaymentSimulatorResponse['data']['schedule']
    | GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse['data']['schedule'];
  showShortLabel?: boolean;
};

export const ExportRepaymentScheduleToCSVButton = ({
  schedule,
  showShortLabel = false,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const { i18n } = useLocale();
  const { formatCurrency } = useNumberFormat();

  const data = schedule.map(
    ({ date, principal, interest, total_fees, total }) => ({
      [t`Date`]: i18n.date(date, {
        day: '2-digit',
        month: 'numeric',
        year: 'numeric',
      }),
      [t`Original loan`]: formatCurrency(principal),
      [t`Fees + interest`]: formatCurrency(interest + total_fees),
      [t`Total`]: formatCurrency(total),
    }),
  );

  // Add a disclaimer below the schedule data with a row in between
  data.push({});
  data.push({
    [t`Date`]: t`Please be aware that interest is charged on a daily basis on your client's outstanding balance. Monthly repayment dates and interest payments may vary.`,
  });

  const timestamp = format(new Date(), 'dd-MM-yyyy_HH-mm-ss');
  const filename = `${t`export`}_${timestamp}`;

  return (
    <CSVLink data={data} filename={filename}>
      <Button
        variant="tertiary"
        data-testid="export-repayment-schedule-csv-button"
        size="small"
        onClick={() => trackEvent(AMPLITUDE_EVENTS.repaymentScheduleCSVExport)}
      >
        <IconDownload />
        {showShortLabel ? 'CSV' : <Trans>Export CSV</Trans>}
      </Button>
    </CSVLink>
  );
};
